const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

// Elements already handed to an observer, so re-scans don't observe them twice
const observedAnimationElements = new WeakSet();

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => {
    if (observedAnimationElements.has(element)) return;
    observedAnimationElements.add(element);
    observer.observe(element);
  });
}

// Safety net: an element keeps `opacity: 0.01` for as long as it carries the
// offscreen class, so anything that ends up flagged without an observer watching
// it (slider clones, innerHTML copies, app-injected or re-rendered sections)
// would stay invisible forever. Reveal whatever is actually on screen.
function revealVisibleScrollTriggers(rootEl = document) {
  rootEl.querySelectorAll(`.${SCROLL_ANIMATION_OFFSCREEN_CLASSNAME}`).forEach((element) => {
    const rect = element.getBoundingClientRect();
    // Skip genuinely hidden elements — they get revealed once they are laid out
    if (rect.width === 0 && rect.height === 0) return;
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    }
  });
}

// Pick up scroll-trigger elements that appear after the initial pass
function watchForNewScrollTriggers() {
  if (!('MutationObserver' in window)) return;

  let pending = null;
  const mutationObserver = new MutationObserver((mutations) => {
    const touchesTrigger = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.classList?.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME) ||
            node.getElementsByClassName?.(SCROLL_ANIMATION_TRIGGER_CLASSNAME).length)
      )
    );
    if (!touchesTrigger || pending) return;

    pending = setTimeout(() => {
      pending = null;
      initializeScrollAnimationTrigger();
      revealVisibleScrollTriggers();
    }, 200);
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  watchForNewScrollTriggers();
});

// Late images and webfonts shift the layout, so re-check once everything settled
window.addEventListener('load', () => {
  initializeScrollAnimationTrigger();
  revealVisibleScrollTriggers();
});

// Throttled backstop for elements an observer never reported on.
// throttle() ignores an undefined delay, so the interval has to be explicit.
window.addEventListener('scroll', throttle(() => revealVisibleScrollTriggers(), 300), { passive: true });

// Sections re-rendered outside the theme editor (filters, cart, apps) need re-observing
document.addEventListener('shopify:section:load', (event) => {
  if (Shopify.designMode) return;
  initializeScrollAnimationTrigger(event.target);
  revealVisibleScrollTriggers(event.target);
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
